// src/pages/driverReports/InstallmentAdjustments.js
import React, { useEffect, useMemo, useState } from "react";
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDocs,
  orderBy,
  query,
  serverTimestamp,
  where,
} from "firebase/firestore";
import { db } from "../../firebase/firebase";

function currentMonth() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

function toDate(value) {
  try {
    if (!value) return null;
    if (typeof value?.toDate === "function") return value.toDate();
    if (value instanceof Date) return value;
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? null : d;
  } catch {
    return null;
  }
}

function dateText(value) {
  const d = toDate(value);
  if (!d) return "";
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(
    d.getDate()
  ).padStart(2, "0")}`;
}

export default function InstallmentAdjustments() {
  const [month, setMonth] = useState(currentMonth());
  const [assignments, setAssignments] = useState([]);
  const [adjustments, setAdjustments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedAssignmentId, setSelectedAssignmentId] = useState("");
  const [form, setForm] = useState({
    adjustmentType: "fixed_discount",
    fromDate: "",
    toDate: "",
    amount: "",
    reason: "",
  });

  const selectedAssignment = useMemo(
    () => assignments.find((a) => a.id === selectedAssignmentId),
    [assignments, selectedAssignmentId]
  );

  const loadData = async () => {
    setLoading(true);
    try {
      const assSnap = await getDocs(query(collection(db, "driverCarAssignments"), orderBy("startDate", "desc")));
      const ass = assSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
      setAssignments(ass.filter((a) => String(a.driverRole || "main").toLowerCase() !== "second"));

      const adjSnap = await getDocs(
        query(collection(db, "driverInstallmentAdjustments"), where("month", "==", month))
      );
      setAdjustments(adjSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
    } catch (err) {
      console.error("Failed to load adjustment data:", err);
      alert("Failed to load data: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [month]);

  const saveAdjustment = async () => {
    if (!selectedAssignment) {
      alert("Please select a main driver assignment.");
      return;
    }
    if (!form.adjustmentType) {
      alert("Please select adjustment type.");
      return;
    }
    if (!form.fromDate) {
      alert("Please select from date.");
      return;
    }
    if (!form.toDate) {
      alert("Please select to date.");
      return;
    }
    if (form.adjustmentType !== "days_relief" && Number(form.amount || 0) === 0) {
      alert("Please enter amount.");
      return;
    }
    if (!form.reason.trim()) {
      alert("Please enter reason / remarks.");
      return;
    }

    try {
      await addDoc(collection(db, "driverInstallmentAdjustments"), {
        month,
        assignmentId: selectedAssignment.id,
        vehicleId: selectedAssignment.vehicleId || "",
        vehicleNumber: selectedAssignment.vehicleNumber || "",
        driverId: selectedAssignment.driverId || "",
        driverName: selectedAssignment.driverName || "",
        company: selectedAssignment.company || "",
        adjustmentType: form.adjustmentType,
        fromDate: form.fromDate,
        toDate: form.toDate,
        amount: Number(form.amount || 0),
        reason: form.reason.trim(),
        status: "active",
        createdAt: serverTimestamp(),
      });

      setForm({ adjustmentType: "fixed_discount", fromDate: "", toDate: "", amount: "", reason: "" });
      await loadData();
      alert("Adjustment saved. It will apply when you rebuild the selected month car sheets.");
    } catch (err) {
      console.error("Failed to save adjustment:", err);
      alert("Failed: " + err.message);
    }
  };

  const deleteAdjustment = async (adj) => {
    if (!window.confirm(`Delete this adjustment for ${adj.driverName || adj.vehicleNumber}?`)) return;
    try {
      await deleteDoc(doc(db, "driverInstallmentAdjustments", adj.id));
      await loadData();
    } catch (err) {
      console.error("Failed to delete adjustment:", err);
      alert("Failed: " + err.message);
    }
  };

  const filteredAssignments = useMemo(() => {
    const mStart = new Date(`${month}-01T00:00:00`);
    const [y, m] = month.split("-").map(Number);
    const mEnd = new Date(y, m, 0, 23, 59, 59, 999);

    return assignments.filter((a) => {
      const s = toDate(a.startDate);
      const e = a.endDate ? toDate(a.endDate) : new Date(9999, 0, 1);
      if (!s) return false;
      return s <= mEnd && e >= mStart;
    });
  }, [assignments, month]);

  const explainType = (type) => {
    if (type === "days_relief") return "Deduct daily installment for selected dates.";
    if (type === "manual_override") return "Replace calculated fixed cost for the matching sheet period.";
    if (type === "extra_charge") return "Add extra amount to the installment row.";
    return "Deduct fixed amount only for this month.";
  };

  return (
    <div style={{ padding: 20, maxWidth: 1200 }}>
      <h2>Monthly Installment Adjustments</h2>
      <p style={{ color: "#555", maxWidth: 900 }}>
        Use this for temporary discounts, visa relief, management relief, manual overrides, or extra charges.
        These records are month-specific, so July relief will not affect August.
      </p>

      <div style={{ display: "flex", gap: 12, alignItems: "center", marginBottom: 16 }}>
        <label>
          Statement Month:{" "}
          <input type="month" value={month} onChange={(e) => setMonth(e.target.value)} />
        </label>
        <button type="button" onClick={loadData} disabled={loading}>{loading ? "Loading..." : "Reload"}</button>
      </div>

      <div style={{ border: "1px solid #e5e7eb", borderRadius: 8, padding: 14, marginBottom: 20 }}>
        <h3 style={{ marginTop: 0 }}>Add Adjustment</h3>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 12 }}>
          <div>
            <label style={{ display: "block", marginBottom: 4 }}>Main Driver Assignment</label>
            <select
              value={selectedAssignmentId}
              onChange={(e) => setSelectedAssignmentId(e.target.value)}
              style={{ width: "100%", padding: 8 }}
            >
              <option value="">Select assignment</option>
              {filteredAssignments.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.vehicleNumber || "No car"} — {a.driverName || "No driver"} — {dateText(a.startDate)} to {a.endDate ? dateText(a.endDate) : "Present"}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label style={{ display: "block", marginBottom: 4 }}>Adjustment Type</label>
            <select
              value={form.adjustmentType}
              onChange={(e) => setForm((f) => ({ ...f, adjustmentType: e.target.value }))}
              style={{ width: "100%", padding: 8 }}
            >
              <option value="fixed_discount">Fixed Discount / Relief Amount</option>
              <option value="days_relief">Relief by Date Range</option>
              <option value="manual_override">Manual Override for Sheet Period</option>
              <option value="extra_charge">Extra Charge</option>
            </select>
            <div style={{ fontSize: 12, color: "#666", marginTop: 4 }}>{explainType(form.adjustmentType)}</div>
          </div>

          <div>
            <label style={{ display: "block", marginBottom: 4 }}>From Date</label>
            <input
              type="date"
              value={form.fromDate}
              onChange={(e) => setForm((f) => ({ ...f, fromDate: e.target.value }))}
              style={{ width: "100%", padding: 8 }}
            />
          </div>

          <div>
            <label style={{ display: "block", marginBottom: 4 }}>To Date</label>
            <input
              type="date"
              value={form.toDate}
              onChange={(e) => setForm((f) => ({ ...f, toDate: e.target.value }))}
              style={{ width: "100%", padding: 8 }}
            />
          </div>

          <div>
            <label style={{ display: "block", marginBottom: 4 }}>
              Amount {form.adjustmentType === "days_relief" ? "(optional)" : ""}
            </label>
            <input
              type="number"
              value={form.amount}
              onChange={(e) => setForm((f) => ({ ...f, amount: e.target.value }))}
              placeholder={form.adjustmentType === "days_relief" ? "System calculates relief" : "AED"}
              style={{ width: "100%", padding: 8 }}
            />
          </div>

          <div>
            <label style={{ display: "block", marginBottom: 4 }}>Reason / Remarks</label>
            <input
              value={form.reason}
              onChange={(e) => setForm((f) => ({ ...f, reason: e.target.value }))}
              placeholder="Visa expired relief, discount, approval, etc."
              style={{ width: "100%", padding: 8 }}
            />
          </div>
        </div>

        <button type="button" onClick={saveAdjustment} style={{ marginTop: 14, padding: "8px 14px" }}>
          Save Adjustment
        </button>
      </div>

      <h3>Adjustments for {month}</h3>
      <div style={{ overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
          <thead>
            <tr style={{ background: "#f3f4f6" }}>
              <th style={{ textAlign: "left", padding: 8 }}>Car</th>
              <th style={{ textAlign: "left", padding: 8 }}>Driver</th>
              <th style={{ textAlign: "left", padding: 8 }}>Type</th>
              <th style={{ textAlign: "left", padding: 8 }}>Dates</th>
              <th style={{ textAlign: "left", padding: 8 }}>Amount</th>
              <th style={{ textAlign: "left", padding: 8 }}>Reason</th>
              <th style={{ textAlign: "left", padding: 8 }}>Action</th>
            </tr>
          </thead>
          <tbody>
            {adjustments.map((a) => (
              <tr key={a.id}>
                <td style={{ borderBottom: "1px solid #e5e7eb", padding: 8 }}>{a.vehicleNumber || "-"}</td>
                <td style={{ borderBottom: "1px solid #e5e7eb", padding: 8 }}>{a.driverName || "-"}</td>
                <td style={{ borderBottom: "1px solid #e5e7eb", padding: 8 }}>{a.adjustmentType || "-"}</td>
                <td style={{ borderBottom: "1px solid #e5e7eb", padding: 8 }}>{a.fromDate || "-"} → {a.toDate || "-"}</td>
                <td style={{ borderBottom: "1px solid #e5e7eb", padding: 8 }}>{Number(a.amount || 0).toFixed(2)}</td>
                <td style={{ borderBottom: "1px solid #e5e7eb", padding: 8 }}>{a.reason || "-"}</td>
                <td style={{ borderBottom: "1px solid #e5e7eb", padding: 8 }}>
                  <button type="button" onClick={() => deleteAdjustment(a)}>Delete</button>
                </td>
              </tr>
            ))}
            {adjustments.length === 0 && (
              <tr>
                <td colSpan={7} style={{ padding: 12, color: "#666" }}>No adjustments for this month.</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
